/**
 * The problems list.
 *
 * Every diagnostic is a button that moves the caret to the offending range —
 * a diagnostic you cannot navigate to is only half a diagnostic. Severity is
 * carried by an icon and by text, never by colour alone.
 */
import { useMemo, useState } from 'react';
import type { Diagnostic, DiagnosticSeverity } from '../language/diagnostics/types';
import { countBySeverity } from '../language/diagnostics/types';
import { AlertCircle, AlertTriangle, Check, Info } from './ui/icons';
import type { Translator } from '../i18n';

export interface ProblemsPanelProps {
  readonly diagnostics: readonly Diagnostic[];
  readonly onSelect: (diagnostic: Diagnostic) => void;
  readonly t: Translator['t'];
}

type Filter = 'all' | DiagnosticSeverity;

const SEVERITY_ICON = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

const SEVERITY_COLOR = {
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
} as const;

export function ProblemsPanel({ diagnostics, onSelect, t }: ProblemsPanelProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const counts = useMemo(() => countBySeverity(diagnostics), [diagnostics]);

  const visible = useMemo(
    () => (filter === 'all' ? diagnostics : diagnostics.filter((d) => d.severity === filter)),
    [diagnostics, filter],
  );

  const filters: readonly { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: t('problems.filterAll'), count: diagnostics.length },
    { key: 'error', label: t('problems.filterErrors'), count: counts.error },
    { key: 'warning', label: t('problems.filterWarnings'), count: counts.warning },
    { key: 'info', label: t('problems.filterInfos'), count: counts.info },
  ];

  return (
    <section className="flex h-full min-h-0 flex-col bg-surface" aria-label={t('problems.title')}>
      <header className="flex shrink-0 flex-wrap items-center gap-1 border-b border-border px-2 py-1.5">
        {filters.map((item) => (
          <button
            key={item.key}
            type="button"
            aria-pressed={filter === item.key}
            onClick={() => setFilter(item.key)}
            className={[
              'rounded px-2 py-1 text-xs font-medium transition-colors',
              filter === item.key
                ? 'bg-accent text-accent-ink'
                : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
            ].join(' ')}
          >
            {item.label}
            <span className="ml-1.5 tabular-nums opacity-70">{item.count}</span>
          </button>
        ))}
      </header>

      {visible.length === 0 ? (
        <p className="flex flex-1 items-center justify-center gap-2 p-6 text-sm text-ink-faint">
          <Check className="size-4 text-success" />
          {t('problems.none')}
        </p>
      ) : (
        <ul className="fs-scrollbar min-h-0 flex-1 overflow-y-auto">
          {visible.map((diagnostic, index) => {
            const Icon = SEVERITY_ICON[diagnostic.severity];
            return (
              <li key={`${diagnostic.code}-${diagnostic.range.start.offset}-${index}`}>
                <button
                  type="button"
                  onClick={() => onSelect(diagnostic)}
                  className="flex w-full gap-2.5 border-b border-border/60 px-3 py-2 text-left transition-colors hover:bg-surface-sunken"
                >
                  <Icon
                    className={`mt-0.5 size-4 shrink-0 ${SEVERITY_COLOR[diagnostic.severity]}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-ink">{diagnostic.message}</span>
                    {diagnostic.suggestion ? (
                      <span className="mt-0.5 block text-xs text-ink-muted">
                        {diagnostic.suggestion}
                      </span>
                    ) : null}
                    <span className="mt-1 block font-mono text-[11px] text-ink-faint">
                      {t('problems.location', {
                        line: diagnostic.range.start.line,
                        column: diagnostic.range.start.column,
                      })}
                      <span className="ml-2 opacity-70">{diagnostic.code}</span>
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
